import React, { useEffect, useRef, useState } from 'react';
import { Camera, Image as ImageIcon, Loader2, RefreshCw, X, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const stopStream = (s) => s?.getTracks().forEach((t) => t.stop());

const BlockedHelp = ({ onRetry, onFallback, testId }) => (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid={`${testId}-blocked`}>
    <p className="font-semibold">Camera is blocked for this site</p>
    <ol className="list-decimal ml-4 space-y-0.5">
      <li>Tap the lock / ⓘ icon left of the web address (top of the screen).</li>
      <li>Tap <b>Permissions</b> (or Site settings) → <b>Camera</b> → <b>Allow</b>.</li>
      <li>Come back here and tap <b>Try again</b>.</li>
    </ol>
    <div className="flex gap-2 pt-1">
      <Button type="button" size="sm" onClick={onRetry} className="bg-amber-500 hover:bg-amber-600" data-testid={`${testId}-retry`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Try again</Button>
      {onFallback && <Button type="button" size="sm" variant="outline" onClick={onFallback} data-testid={`${testId}-blocked-fallback`}><ImageIcon className="w-3.5 h-3.5 mr-1" />Use phone camera / gallery</Button>}
    </div>
  </div>
);

/**
 * In-page camera (getUserMedia). The browser never leaves the page, so low-memory
 * phones can't kill it while the photo is taken (the native camera app does that).
 * onShot(dataUrl) gets a JPEG already shrunk to maxEdge px.
 */
export const InAppCamera = ({ onShot, onCancel, onFallback, maxEdge = 1280, quality = 0.7, testId = 'in-app-camera', facing = 'environment' }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [state, setState] = useState('starting'); // starting | live | blocked | unavailable
  const [torch, setTorch] = useState(null); // null = not supported
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setState('starting');
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setState('unavailable'); return; }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } } });
        if (!alive) { stopStream(s); return; }
        streamRef.current = s;
        const v = videoRef.current;
        if (v) { v.srcObject = s; await v.play().catch(() => {}); }
        const caps = s.getVideoTracks()[0]?.getCapabilities?.() || {};
        setTorch(caps.torch ? false : null);
        setState('live');
      } catch (e) {
        if (alive) setState(['NotAllowedError', 'SecurityError', 'PermissionDeniedError'].includes(e?.name) ? 'blocked' : 'unavailable');
      }
    })();
    return () => { alive = false; stopStream(streamRef.current); streamRef.current = null; };
  }, [attempt, facing]);

  const toggleTorch = async () => {
    const t = streamRef.current?.getVideoTracks()[0];
    try { await t.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { setTorch(null); }
  };

  const shoot = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const scale = Math.min(1, maxEdge / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    const url = c.toDataURL('image/jpeg', quality);
    stopStream(streamRef.current); streamRef.current = null;
    onShot(url);
  };

  if (state === 'blocked') return <BlockedHelp onRetry={() => setAttempt((a) => a + 1)} onFallback={onFallback} testId={testId} />;
  if (state === 'unavailable') {
    return (
      <div className="rounded-lg border bg-gray-50 p-3 text-xs text-gray-700 space-y-2" data-testid={`${testId}-unavailable`}>
        <p>The in-app camera isn't available on this phone.</p>
        {onFallback && <Button type="button" size="sm" variant="outline" onClick={onFallback} data-testid={`${testId}-unavailable-fallback`}><ImageIcon className="w-3.5 h-3.5 mr-1" />Use phone camera / gallery</Button>}
      </div>
    );
  }
  return (
    <div className="rounded-lg overflow-hidden bg-black relative" data-testid={testId}>
      <video ref={videoRef} playsInline muted autoPlay className="w-full max-h-[55vh] object-contain bg-black" style={facing === 'user' ? { transform: 'scaleX(-1)' } : undefined} data-testid={`${testId}-video`} />
      {state === 'starting' && <div className="absolute inset-0 flex items-center justify-center text-white text-sm"><Loader2 className="w-5 h-5 mr-2 animate-spin" />Opening camera…</div>}
      <div className="flex items-center justify-between gap-2 p-2 bg-black/80">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="text-white hover:bg-white/10" data-testid={`${testId}-cancel`}><X className="w-4 h-4" /></Button>
        <Button type="button" onClick={shoot} disabled={state !== 'live'} className="h-12 px-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid={`${testId}-shoot`}><Camera className="w-5 h-5 mr-2" />Capture</Button>
        {torch !== null
          ? <Button type="button" size="sm" variant="ghost" onClick={toggleTorch} className={torch ? 'text-amber-300 hover:bg-white/10' : 'text-white hover:bg-white/10'} data-testid={`${testId}-torch`}><Zap className="w-4 h-4" /></Button>
          : <span className="w-9" />}
      </div>
    </div>
  );
};

export default InAppCamera;
